import { useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { externalMembershipLabel } from '@brimo/shared';
import { clsx } from '../../../../ds/clsx';
import { Banner, Button, Pill, Skeleton, Toggle, Tooltip, useToast } from '../../../../ds/components';
import { errorMessage } from '../../../../lib/api';
import { useRealtime } from '../../../../lib/events';
import { useWorkspace } from '../../../../lib/session';
import {
  useUsersTable,
  usePatchUser,
  useReactivateUser,
  useResendInvitation,
  userKeys,
  type UserTableRow,
} from './api';
import { DeactivateDialog } from './DeactivateDialog';
import { EditUserDialog } from './EditUserDialog';
import { InviteDialog } from './InviteDialog';
import { OrganizationPage, QueryError } from './OrganizationPage';
import { ToolPills } from './ToolPills';
import { SERVICING_LOCKED_TEXT } from './tools';
import styles from './UsersPage.module.css';

const INTRO_FALLBACK = 'Nutzer, Rollen und die Abrechnung je Arbeitsplatz.';

function statusClass(row: UserTableRow): string | undefined {
  if (row.status === 'invited') {
    return row.statusLabel === 'Einladung offen' ? styles.statusInvited : styles.statusExpired;
  }
  return row.status === 'active' ? styles.statusActive : styles.statusDeactivated;
}

/**
 * „Nutzer und Rollen" (Einstellungen 27): the organization's users and open invitations with role, status and
 * the own-billing switch (`PATCH /users/:id { hasOwnBilling }`), the row action „Bearbeiten" (EditUserDialog →
 * „Rechte anpassen" / „Deaktivieren …" = Admin 06), „Erneut senden", „Reaktivieren"; „+ Nutzer einladen".
 */
export function UsersPage() {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const workspace = useWorkspace();
  const table = useUsersTable();
  const patch = usePatchUser();
  const reactivate = useReactivateUser();
  const resend = useResendInvitation();
  const [inviteOpen, setInviteOpen] = useState(false);
  const [editing, setEditing] = useState<UserTableRow | null>(null);
  const [deactivating, setDeactivating] = useState<UserTableRow | null>(null);
  const [banner, setBanner] = useState<string | null>(null);
  const [pendingId, setPendingId] = useState<string | null>(null);
  const data = table.data;

  // The table carries the open invitations too; their events use their own root.
  useRealtime('invitation.updated', () => {
    void queryClient.invalidateQueries({ queryKey: userKeys.table() });
  });

  function toggleBilling(row: UserTableRow, value: boolean) {
    setBanner(null);
    setPendingId(row.id);
    patch.mutate(
      { id: row.id, body: { hasOwnBilling: value } },
      {
        onError: (error) => setBanner(errorMessage(error)),
        onSettled: () => setPendingId(null),
      },
    );
  }

  function runAction(row: UserTableRow) {
    setBanner(null);
    if (row.action.key === 'edit') {
      setEditing(row);
      return;
    }
    if (row.action.key === 'resend') {
      setPendingId(row.id);
      resend.mutate(row.id, {
        onSuccess: () => toast({ message: `Die Einladung an ${row.email} ist erneut unterwegs.` }),
        onError: (error) => setBanner(errorMessage(error)),
        onSettled: () => setPendingId(null),
      });
      return;
    }
    if (row.action.key === 'reactivate') {
      setPendingId(row.id);
      reactivate.mutate(row.id, {
        onSuccess: () => toast({ message: `${row.name} ist wieder aktiv.` }),
        onError: (error) => setBanner(errorMessage(error)),
        onSettled: () => setPendingId(null),
      });
    }
  }

  return (
    <OrganizationPage
      title="Nutzer und Rollen"
      description={data?.headline ?? (table.isPending ? null : INTRO_FALLBACK)}
      actions={
        data?.actions.invite.allowed ? (
          <Button onClick={() => setInviteOpen(true)}>+ Nutzer einladen</Button>
        ) : null
      }
    >
      {table.isError ? <QueryError error={table.error} /> : null}
      {banner ? <Banner tone="danger">{banner}</Banner> : null}
      {table.isPending ? (
        <div className={styles.table} aria-busy="true">
          {[0, 1, 2, 3].map((index) => (
            <div key={index} className={styles.skeletonRow}>
              <Skeleton width={index % 2 === 0 ? '40%' : '55%'} />
            </div>
          ))}
        </div>
      ) : null}
      {data ? (
        <>
          <div className={styles.table} role="table" aria-label="Nutzer und Rollen">
            <div className={clsx(styles.row, styles.head)} role="row">
              <span role="columnheader">Nutzer</span>
              <span role="columnheader">Rolle</span>
              <span role="columnheader">Status</span>
              <span role="columnheader">Eigene Abrechnung</span>
              <span role="columnheader">Tools</span>
              <span role="columnheader" aria-label="Aktion" />
            </div>
            {data.rows.map((row) => (
              <div
                key={`${row.kind}-${row.id}`}
                role="row"
                aria-label={row.name}
                className={clsx(styles.row, row.status === 'deactivated' && styles.rowInactive)}
              >
                <span role="cell" className={styles.person}>
                  <span className={styles.avatar} aria-hidden="true" />
                  <span className={styles.personText}>
                    <span className={styles.name}>{row.name}</span>
                    {row.externalCompany ? (
                      <Pill className={styles.external}>{externalMembershipLabel(row.externalCompany)}</Pill>
                    ) : null}
                    {row.servicingFrom ? (
                      <Pill variant="info" className={styles.external}>
                        Servicing · {row.servicingFrom.name}
                      </Pill>
                    ) : null}
                    <span className={styles.email}>{row.email}</span>
                  </span>
                </span>
                <span role="cell" className={styles.role}>
                  {row.roleLabel}
                </span>
                <span role="cell" className={clsx(styles.status, statusClass(row))}>
                  <span className={styles.statusDot} aria-hidden="true" />
                  {row.statusLabel}
                </span>
                <span role="cell" className={styles.billing}>
                  {row.status === 'deactivated' ? (
                    <span className={styles.billingNone}>{row.ownBilling.label}</span>
                  ) : (
                    <>
                      <Toggle
                        size="large"
                        label={`Eigene Abrechnung ${row.name}`}
                        labelHidden
                        checked={row.ownBilling.value}
                        locked={row.ownBilling.locked || row.servicingFrom !== null}
                        title={
                          row.servicingFrom ? SERVICING_LOCKED_TEXT : (row.ownBilling.reason ?? undefined)
                        }
                        disabled={pendingId === row.id}
                        onChange={(value) => toggleBilling(row, value)}
                      />
                      <span
                        className={clsx(styles.billingLabel, row.ownBilling.value && styles.billingLabelOn)}
                      >
                        {row.ownBilling.label}
                      </span>
                    </>
                  )}
                </span>
                <span role="cell">
                  <ToolPills tools={row.tools} modules={workspace.modules} />
                </span>
                <span role="cell" style={{ justifySelf: 'end' }}>
                  {row.servicingFrom && row.action.key !== 'self' ? (
                    <Tooltip content={SERVICING_LOCKED_TEXT}>
                      <button
                        type="button"
                        className={clsx(styles.action, styles.actionLocked)}
                        aria-disabled="true"
                        aria-label={`${row.action.label}: ${row.name}. ${SERVICING_LOCKED_TEXT}`}
                        onClick={() => setBanner(SERVICING_LOCKED_TEXT)}
                      >
                        {row.action.label}
                      </button>
                    </Tooltip>
                  ) : row.action.key === 'self' || !row.action.allowed ? (
                    <span className={styles.actionStatic}>{row.action.label}</span>
                  ) : (
                    <button
                      type="button"
                      className={styles.action}
                      disabled={pendingId === row.id}
                      aria-label={`${row.action.label}: ${row.name}`}
                      onClick={() => runAction(row)}
                    >
                      {row.action.label}
                    </button>
                  )}
                </span>
              </div>
            ))}
          </div>
          <p className={styles.footer}>{data.footer}</p>
        </>
      ) : null}
      <InviteDialog open={inviteOpen} onClose={() => setInviteOpen(false)} />
      <EditUserDialog
        row={editing}
        onClose={() => setEditing(null)}
        onDeactivate={(row) => {
          setEditing(null);
          setDeactivating(row);
        }}
      />
      <DeactivateDialog row={deactivating} onClose={() => setDeactivating(null)} />
    </OrganizationPage>
  );
}
